import React, { useEffect, useState, useCallback } from 'react';
import { creatorVh2hAPI } from '../api/endpoints';
import { FaPlus, FaPlay, FaTimes, FaCopy, FaFire, FaUsers, FaTrophy } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreatorVirtualH2H.css';

const CreatorVirtualH2H = () => {
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [minStake, setMinStake] = useState(10);
  const [runningId, setRunningId] = useState(null);
  const [unclaimed, setUnclaimed] = useState({ total: 0, items: [] });
  const [claimingId, setClaimingId] = useState(null);

  const fetchMine = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await creatorVh2hAPI.listMine();
      setGames(res.data?.data || []);
    } catch (err) {
      if (showLoader) toast.error('Failed to load your games');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);


  const fetchUnclaimed = useCallback(async () => {
  try {
    const res = await creatorVh2hAPI.getMyUnclaimed();
    const data = res.data || {};
    setUnclaimed({
      total: Number(data.total_unclaimed || 0),
      items: Array.isArray(data.items) ? data.items : [],
    });
  } catch (err) {
    // Silent fail — banner just won't show
    console.warn('[CVH2H] unclaimed fetch failed:', err);
  }
}, []);

useEffect(() => {
  fetchMine(true);
  fetchUnclaimed();
}, [fetchMine, fetchUnclaimed]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await creatorVh2hAPI.create({ min_stake: Number(minStake) });
      if (res.data?.success) {
        toast.success('Virtual match created! Share the link.');
        setShowCreate(false);
        setMinStake(10);
        fetchMine(false);
      } else {
        toast.error(res.data?.message || 'Failed to create');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleRun = async (game) => {
    if (!window.confirm(`Run "${game.home_team} vs ${game.away_team}" now?`)) return;
    setRunningId(game.id);
    try {
      const res = await creatorVh2hAPI.run(game.id);
      if (res.data?.success) {
        const outcome = res.data.outcome;
        if (outcome === 'cancelled') toast('No players — game cancelled', { icon: '⚠️' });
        else if (outcome === 'refunded') toast('Only one side — refunded', { icon: '💸' });
        else toast.success('Match settled!');
        fetchMine(false);
      } else {
        toast.error(res.data?.message || 'Failed to run');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to run');
    } finally {
      setRunningId(null);
    }
  };

  const handleCancel = async (game) => {
    if (!window.confirm('Cancel this game and refund all stakes?')) return;
    try {
      const res = await creatorVh2hAPI.cancel(game.id);
      if (res.data?.success) {
        toast.success('Cancelled — refunds issued');
        fetchMine(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel');
    }
  };

const handleClaim = async (item) => {
  setClaimingId(item.game_id);
  try {
    const res = await creatorVh2hAPI.claim(item.game_id);
    if (res.data?.success) {
      toast.success(`Claimed ${Number(res.data.amount).toFixed(2)} $XPUNT`);
      await fetchUnclaimed();
      await fetchMine(false);
    } else {
      toast.error(res.data?.message || 'Failed to claim');
    }
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to claim');
  } finally {
    setClaimingId(null);
  }
};

  const copyLink = (game) => {
    navigator.clipboard.writeText(game.share_url);
    toast.success('Link copied!');
  };

  return (
    <div className="cvh2h-page">
      <div className="cvh2h-header">
        <h1>⚡ Your Virtual H2H</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <FaPlus /> New Virtual Match
        </button>
      </div>

      {unclaimed.items.length > 0 && (
  <div className="unclaimed-banner">
    <div className="unclaimed-header">
      <div className="unclaimed-icon-wrap">
        <FaMoneyBillWave />
      </div>
      <div className="unclaimed-text">
        <h3>You have unclaimed winnings!</h3>
        <p>
          {unclaimed.items.length} match{unclaimed.items.length !== 1 ? 'es' : ''} ·{' '}
          <strong>{unclaimed.total.toFixed(2)} $XPUNT</strong> waiting
        </p>
      </div>
    </div>

    <div className="unclaimed-list">
      {unclaimed.items.map((item) => (
        <div key={item.stake_id} className="unclaimed-item">
          <div className="unclaimed-item-teams">
            <div className="unclaimed-team">
              {item.home_club_logo && <img src={item.home_club_logo} alt="" />}
              <span>{item.home_team}</span>
              {item.home_score !== null && <strong>{item.home_score}</strong>}
            </div>
            <span className="unclaimed-vs">vs</span>
            <div className="unclaimed-team away">
              {item.away_club_logo && <img src={item.away_club_logo} alt="" />}
              <span>{item.away_team}</span>
              {item.away_score !== null && <strong>{item.away_score}</strong>}
            </div>
          </div>

          <div className="unclaimed-item-meta">
            <span className="unclaimed-result">{item.result}</span>
            <span className="unclaimed-amount">
              {Number(item.winnings).toFixed(2)} $XPUNT
            </span>
          </div>

          <button
            className="unclaimed-claim-btn"
            onClick={() => handleClaim(item)}
            disabled={claimingId === item.game_id}
            type="button"
          >
            {claimingId === item.game_id ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <FaCheckCircle /> Claim
              </>
            )}
          </button>
        </div>
      ))}
    </div>
  </div>
)}

      {showCreate && (
        <form className="cvh2h-create-form" onSubmit={handleCreate}>
          <h3>Create a Virtual Match</h3>
          <p className="form-hint">
            The system will randomly pick two clubs. You just set the minimum stake.
          </p>
          <div className="form-group">
            <label>Minimum Stake ($XPUNT)</label>
            <input
              type="number" min="1" step="1"
              value={minStake}
              onChange={(e) => setMinStake(e.target.value)}
              autoFocus
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? <LoadingSpinner size="small" /> : 'Generate Match'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="cvh2h-loading"><LoadingSpinner size="large" /></div>
      ) : games.length === 0 ? (
        <div className="cvh2h-empty">
          <FaFire className="empty-icon" />
          <p>No virtual matches yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="cvh2h-list">
          {games.map(g => (
            <div key={g.id} className={`cvh2h-card status-${g.status}`}>
              <div className="card-top">
                <span className="league-tag">{g.league}</span>
                <span className={`status-tag ${g.status}`}>{g.status.toUpperCase()}</span>
              </div>

              <div className="teams">
                <div className="team">
                  {g.home_club_logo && <img src={g.home_club_logo} alt="" />}
                  <span>{g.home_team}</span>
                  {g.home_score !== null && <strong className="score">{g.home_score}</strong>}
                </div>
                <span className="vs">vs</span>
                <div className="team away">
                  {g.away_club_logo && <img src={g.away_club_logo} alt="" />}
                  <span>{g.away_team}</span>
                  {g.away_score !== null && <strong className="score">{g.away_score}</strong>}
                </div>
              </div>

              <div className="card-stats">
                <span><FaUsers /> {g.total_players} players</span>
                <span><FaTrophy /> {Number(g.total_pot).toFixed(2)} $XPUNT</span>
                <span className="min-stake">Min: {g.min_stake}</span>
              </div>

              {g.status === 'open' && (
                <div className="card-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => copyLink(g)}>
                    <FaCopy /> Copy Link
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleCancel(g)}>
                    <FaTimes /> Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => handleRun(g)} disabled={runningId === g.id}>
                    {runningId === g.id ? <LoadingSpinner size="small" /> : <><FaPlay /> Run & Simulate</>}
                  </button>
                </div>
              )}

              {g.status === 'open' && g.total_players === 0 && (
                <div className="hint">
                  Waiting for players. Share the link — the game stays open until you click Run.
                </div>
              )}

              {g.status === 'completed' && g.result && (
                <div className="result-banner">
                  FT: {g.home_team} {g.home_score} - {g.away_score} {g.away_team} — <strong>{g.result}</strong>
                </div>
              )}

              {g.status === 'completed' && g.settlement_summary && (
                <div className="settlement-summary">
                  <div className="summary-row">
                    <span>Winner pool payout</span>
                    <strong>{Number(g.settlement_summary.total_winner_payout).toFixed(2)} $XPUNT</strong>
                  </div>
                  <div className="summary-row">
                    <span>Total fee collected</span>
                    <strong>{Number(g.settlement_summary.total_fee_collected).toFixed(2)} $XPUNT</strong>
                  </div>
                  <div className="summary-row highlight">
                    <span>💰 You earned (7%)</span>
                    <strong>{Number(g.settlement_summary.creator_share).toFixed(2)} $XPUNT</strong>
                  </div>
                  <div className="summary-row muted">
                    <span>Platform (3%)</span>
                    <span>{Number(g.settlement_summary.admin_share).toFixed(2)} $XPUNT</span>
                  </div>
                </div>
              )}

              {g.status === 'refunded' && <div className="result-banner refunded">💸 Refunded — only one side had players.</div>}
              {g.status === 'cancelled' && <div className="result-banner cancelled">❌ Cancelled — no players.</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorVirtualH2H;